import { AxiosInstanceEnum, axiosManager } from '@jooxter/api';
import { FullCalendarViewEnum } from '@jooxter/fullcalendar';
import { CookieService, CookieOptions } from '@jooxter/storage';
import { now, toISO } from '@jooxter/utils';
import { SwitchViewEnum } from '../components';
import { queryClient } from '../react-query.client';
import { defaultResourceFiltersRange, defaultColleagueFiltersRange } from './common';
import { FILTERS_STORAGE_TOKEN } from './constants';
import { FilterContext, FoldedFilterContext, JooxterState } from './types';
import { StateCreator } from 'zustand';
import { defaultFilters } from './default.filters';

export enum StoreEnum {
  'default',
  'rtsm',
}

export const stateFactory = (
  set: Parameters<StateCreator<JooxterState>>[0],
  store = StoreEnum.default
): JooxterState => {
  const defaultState = {
    filter: defaultFilters,
    setFilter: (filter: FilterContext) => set(() => ({ filter })),
    token: undefined,
    setToken: (token: string) => {
      set(() => ({ token }));
    },
    deleteToken: () => {
      if (Array.isArray(window.jimo) && window.jimoKill) {
        // call kill on logout
        window.jimoKill();
      }
      // we don't want to clear all the localStorage because it bugs jimo
      localStorage.removeItem(FILTERS_STORAGE_TOKEN);
      CookieService.deleteCookie('email');
      queryClient.removeQueries();
      set(() => ({
        token: undefined,
        axiosInstance: AxiosInstanceEnum.axiosInstanceV3,
        email: '',
        filter: defaultFilters,
        spaceSearchView: SwitchViewEnum.Floorplan,
      }));
    },
    email: '',
    setEmail: (email?: string, options?: CookieOptions) =>
      set(() => {
        if (email) {
          // we save email in cookie to be able to retrieve it after a subdomain change (ie fr.app.jooxter.com to en.app.jooxter.com)
          CookieService.setCookie('email', email.toLowerCase(), options);
        } else {
          CookieService.deleteCookie('email', options);
        }
        return { email: email?.toLowerCase() };
      }),
    foldedFilters: {
      analytics: { spaceType: false },
      spaces: {
        date: false,
        spaceType: true,
        labels: true,
      },
      colleagues: {
        teams: false,
      },
    },
    setFoldedFilters: (foldedFilters: FoldedFilterContext) => set(() => ({ foldedFilters })),
    spaceSearchView: SwitchViewEnum.Floorplan,
    setSpaceSearchView: (spaceSearchView: SwitchViewEnum) => set(() => ({ spaceSearchView })),
    spaceSchedulerView: FullCalendarViewEnum.ResourceTimelineDay,
    setSpaceSchedulerView: (spaceSchedulerView: FullCalendarViewEnum) => set(() => ({ spaceSchedulerView })),
    spaceSchedulerWholeDay: false,
    setSpaceSchedulerWholeDay: (spaceSchedulerWholeDay: boolean) => set(() => ({ spaceSchedulerWholeDay })),
    // Detailed by default, as the month always was: compact on demand
    spaceSchedulerMonthCompact: false,
    setSpaceSchedulerMonthCompact: (spaceSchedulerMonthCompact: boolean) => set(() => ({ spaceSchedulerMonthCompact })),
    spaceFiltersCollapsed: false,
    setSpaceFiltersCollapsed: (spaceFiltersCollapsed: boolean) => set(() => ({ spaceFiltersCollapsed })),
    myBookingsView: FullCalendarViewEnum.DayGridMonth,
    setMyBookingsView: (myBookingsView: FullCalendarViewEnum) => set(() => ({ myBookingsView })),
    resetFilterFromAndTo: (timezone: string) =>
      set((state: JooxterState) => ({
        filter: { ...state.filter, from: toISO(now(timezone).startOf('week')), to: toISO(now(timezone).endOf('week')) },
      })),
    resetResourceFilter: (userId: number) =>
      set((state: JooxterState) => ({
        filter: {
          ...state.filter,
          resource: {
            ...defaultFilters.resource,
            date: defaultResourceFiltersRange(),
            now: true,
            organizerId: userId,
          },
        },
      })),
    resetResourceFilterDate: () =>
      set((state: JooxterState) => ({
        filter: {
          ...state.filter,
          resource: {
            ...state.filter.resource,
            date: defaultResourceFiltersRange(),
            now: true,
          },
        },
      })),
    resetColleagueFilter: () =>
      set((state: JooxterState) => ({
        filter: {
          ...state.filter,
          colleague: {
            ...defaultFilters.colleague,
            date: defaultColleagueFiltersRange(),
          },
        },
      })),
    highligthedFloorplanPicto: undefined,
    setHighligthedFloorplanPicto: (floorplanPictoId?: number) =>
      set(() => ({ highligthedFloorplanPicto: floorplanPictoId })),
    axiosInstanceBlocked: false,
    blockAxiosInstance: (block: boolean) => set(() => ({ axiosInstanceBlocked: block })),
    axiosInstance: AxiosInstanceEnum.axiosInstanceV3,
    setAxiosInstance: (axiosInstance: AxiosInstanceEnum) =>
      set((state: JooxterState) => {
        if (!state.axiosInstanceBlocked) {
          axiosManager.setInstance(axiosInstance);

          return { axiosInstance };
        }

        return { axiosInstance: state.axiosInstance };
      }),
  };
  const rtsmState = {
    deleteToken: () => {
      if (Array.isArray(window.jimo) && window.jimoKill) {
        // call kill on logout
        window.jimoKill();
      }
      // Do not touch localStorage nor cookie
      queryClient.removeQueries();
      set(() => ({
        token: undefined,
        axiosInstance: AxiosInstanceEnum.axiosInstanceV3,
        email: '',
      }));
    },
    email: '',
    // no persistence of email in cookie
    setEmail: (email?: string) => set(() => ({ email: email?.toLowerCase() })),
  };

  let mergedState: JooxterState = defaultState;

  switch (store) {
    case StoreEnum.default:
      mergedState = defaultState;
      break;

    case StoreEnum.rtsm:
      mergedState = Object.assign(defaultState, rtsmState);
      break;

    default:
      mergedState = defaultState;
      break;
  }

  return mergedState;
};
